import { Injectable, inject } from '@angular/core';

import { Observable, catchError, combineLatest, map, of, shareReplay, take, tap } from 'rxjs';

import { FinHubNavigationService } from '@hdms/financial-hub/shared';
import { FavoritesNavigationService } from '@hdms/myaccount-favorites/shared';
import { ProfileNavigationService } from '@hdms/myaccount-profile/shared';
import { TpmNavigationService } from '@hdms/myaccount-tools/shared';
import { OptionalTranslationDirectiveAbstract } from '@hdms/shared/feature';
import { BaseNavLink, BaseNavLinksObservable, UserAccountMenuType } from '@hdms/shared/page-frame/types';
import { EnvironmentService, StorageService, StorageType } from '@hdms/shared/util-core';

export enum AccountLinkSectionKey {
  USER_PROFILE = 'user-profile',
  DOCUMENTS = 'documents',
  TOOLS = 'tools',
  FAVORITES = 'favorites'
}

export const accountLinkSectionMenuType = {
  [AccountLinkSectionKey.USER_PROFILE]: UserAccountMenuType.USER_PROFILE,
  [AccountLinkSectionKey.DOCUMENTS]: UserAccountMenuType.DOCUMENTS,
  [AccountLinkSectionKey.TOOLS]: UserAccountMenuType.TOOLS,
  [AccountLinkSectionKey.FAVORITES]: UserAccountMenuType.FAVS
};

export interface AccountLinkSection {
  id: AccountLinkSectionKey;
  title: string;
  links$?: BaseNavLinksObservable;
  links?: BaseNavLink[];
}

export enum AccountLinksStorageKey {
  TPM = 'acc-lnk-tpm',
  FINHUB = 'acc-lnk-finhub',
  FAVORITES = 'acc-link-favorites',
  PROFILE = 'acc-link-profile'
}

@Injectable()
export class AccountLinksService extends OptionalTranslationDirectiveAbstract {
  private readonly tpmNavigationService = inject(TpmNavigationService);
  private readonly finHubNavigationService = inject(FinHubNavigationService);
  private readonly favoritesNavigationService = inject(FavoritesNavigationService);
  private readonly profileNavigationService = inject(ProfileNavigationService);
  // TODO: un-comment once Auth Mgmt is refactored and back alive
  // private readonly authMgmtNavigationService = inject(AuthMgmtNavigationService);
  private readonly storageService = inject(StorageService);
  private readonly env = inject(EnvironmentService);

  public readonly tpmLinks$ = this.getTpmLinks$();
  public readonly favoritesLinks$ = this.getFavoritesLinks$();
  public readonly documentsLinks$ = this.getDocumentsLinks$();
  public readonly userProfileLinks$ = this.getProfileLinks$();

  public readonly accountLinks$ = this.getAccountLinks$();

  private getProfileLinks$(): BaseNavLinksObservable {
    return this.getLinks$(
      () =>
        combineLatest([
          this.profileNavigationService.getNavigation$(),
          // TODO: remove dummy array and un-comment once Auth Mgmt is refactored and back alive
          of([]) // this.authMgmtNavigationService.getNavigation$()
        ]).pipe(
          map(([profileTabs, authMgmtTabs]) =>
            [...profileTabs, ...authMgmtTabs].map(tab => ({
              url: tab.url,
              title: this.getLabel(tab.label, 'account')
            }))
          )
        ),
      this.getLangKey(AccountLinksStorageKey.PROFILE)
    );
  }

  private getTpmLinks$(): BaseNavLinksObservable {
    return this.getLinks$(
      () =>
        this.tpmNavigationService.getNavigation$().pipe(
          map(tabs =>
            tabs.map(tab => ({
              url: tab.url,
              title: this.getLabel(tab.label, 'toolManagement')
            }))
          )
        ),
      this.getLangKey(AccountLinksStorageKey.TPM)
    );
  }

  private getDocumentsLinks$(): BaseNavLinksObservable {
    return this.getLinks$(
      () =>
        this.finHubNavigationService.getNavigation$().pipe(
          map(tabs =>
            tabs.map(tab => ({
              url: tab.url,
              title: this.getLabel(tab.label, 'documents')
            }))
          )
        ),
      this.getLangKey(AccountLinksStorageKey.FINHUB)
    );
  }

  private getFavoritesLinks$(): BaseNavLinksObservable {
    return this.getLinks$(
      () =>
        this.favoritesNavigationService.getNavigation$().pipe(
          map(tabs =>
            tabs.map(tab => ({
              url: tab.url,
              title: this.getLabel(tab.label, 'favorites')
            }))
          )
        ),
      this.getLangKey(AccountLinksStorageKey.FAVORITES)
    );
  }

  private getLinks$(obsFactory: () => BaseNavLinksObservable, key: string): BaseNavLinksObservable {
    return this.storageService.hasItem(key, StorageType.SESSION)
      ? of(this.storageService.getItem(key, StorageType.SESSION))
      : obsFactory().pipe(
          // exclude links without url value, as well as without label translation,
          // as visibility can be controlled via weblate translation availability.
          map(links => links.filter(link => link.title && link.url)),
          catchError(_ => of([])),
          tap(links => this.storageService.setItem(key, links, StorageType.SESSION)),
          take(1)
        );
  }

  private getLangKey(key: AccountLinksStorageKey): string {
    return `${key}-${this.env.language}`;
  }

  private getLabel(label: string, section: string): string {
    const tLabel = `header.${section}.navigation.${label}`;
    // check if there exists translation, otherwise return null
    return this.checkLabel(tLabel) ? this.i18next.transform(tLabel) : null;
  }

  private getAccountLinks(): AccountLinkSection[] {
    return [
      {
        id: AccountLinkSectionKey.USER_PROFILE,
        title: this.i18next.transform('header.account.title'),
        links$: this.userProfileLinks$
      },
      {
        id: AccountLinkSectionKey.DOCUMENTS,
        title: this.i18next.transform('header.documents.title'),
        links$: this.documentsLinks$
      },
      {
        id: AccountLinkSectionKey.TOOLS,
        title: this.i18next.transform('header.toolManagement.title'),
        links$: this.tpmLinks$
      },
      {
        id: AccountLinkSectionKey.FAVORITES,
        title: this.i18next.transform('header.favorites.title'),
        links$: this.favoritesLinks$
      }
    ];
  }

  private getAccountLinks$(): Observable<AccountLinkSection[]> {
    const accountLinks = this.getAccountLinks();
    return combineLatest(accountLinks.map(({ links$ }) => links$)).pipe(
      map(links =>
        accountLinks
          .map((section, i) => ({
            ...section,
            links: links[i]
          }))
          .filter(section => section.links.length)
      ),
      shareReplay(1)
    );
  }
}
